import React, { useRef } from "react"
import PropTypes from "prop-types"
import { useBuildData } from "gatsby-theme-blank/src/hooks/use-build-data"
import Lightbox from "yet-another-react-lightbox"
import "yet-another-react-lightbox/styles.css"
import NonStretchedImage from "gatsby-theme-blank/src/components/Image/non-stretched-image"
import {
  imagesContainer,
  btnOpener,
  captionContainer,
} from "./light-box-image.module.scss"

import {
  PrevButtonStyled,
  NextButtonStyled,
  CarouselOpenerButton,
} from "./light-box-image-styled"

import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen"
import Zoom from "yet-another-react-lightbox/plugins/zoom"
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails"
import "yet-another-react-lightbox/plugins/thumbnails.css"
import { graphql, useStaticQuery } from "gatsby"
import ImageFromDrupal from "../Image/image-from-drupal"
import Masonry from "react-masonry-css"
import { breakpointColumnsObj } from "gatsby-theme-blank/src/components/Home/GalerieImages/galerie-breakpoints"

/**
 * LightBoxImage display images with lightbox.
 *
 * @param {string} type - The type of base images. Must be 'node', 'fromDrupal', 'carousel' or 'masonry'.
 * @param {string} node.file - Image file from drupal (internal)
 * @param {string} node.src - Image link (external)
 * @param {string} node.id - Image id from drupal
 * @param {string} node.images - Images for masonry display
 */
const LightBoxImage = ({ node, type }) => {
  let themeColors =
    useBuildData().theme_settings.palette.a_color_gradients.a_dark
  themeColors =
    themeColors.toUpperCase() === "#FFFFFF"
      ? useBuildData().theme_settings.palette.c_color_gradients.c_dark
      : themeColors
  const [open, setOpen] = React.useState(false)
  const slides = []
  if (type === "fromDrupal") {
    const image = drupalImageContent(node.id)
    if (image !== null) {
      slides.push({
        src: image.node.localFile.SlugifiedPublicUrl,
        alt: node.alt,
        height: "700",
        imageFit: "contain",
      })
    }
  }
  if (type === "node") {
    slides.push({
      src: node.file ? node.file.SlugifiedPublicUrl : node.src,
      alt: node.alt,
      height: "700",
      imageFit: "contain",
    })
  }
  if (type === "masonry") {
    node.images.forEach((image) => {
      if (node !== null) {
        slides.push({
          src: image.props.SlugifiedPublicUrl,
          alt: image.props.alt,
          height: "700",
          imageFit: "contain",
        })
      }
    })
  }
  const lightboxRef = useRef(null)

  if (typeof document !== `undefined`) {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((element) => {
            if (
              element.nodeType === element.ELEMENT_NODE &&
              element.classList.contains("yarl__root")
            ) {
              element.setAttribute("role", "dialog")
              element.setAttribute("aria-modal", true)
              element.setAttribute("aria-label", "Visionneuse d'images")
            }
          })
        }
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })
  }

  return (
    <div className={imagesContainer} style={node.containerStyle}>
      {type === "node" && (
        <>
          <button
            className={btnOpener}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir l'image en pleine taille"
            title="Ouvrir l'image en pleine taille"
            data-lightbox={true}
          >
            {node.file ? (
              <NonStretchedImage
                alt={node.alt}
                style={node.style}
                {...node.file}
                ariaHidden={true}
              />
            ) : (
              <img style={node.style} alt={node.alt} src={slides[0].src} />
            )}
          </button>
        </>
      )}

      {type === "fromDrupal" && (
        <>
          <button
            className={btnOpener}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir l'image en pleine taille"
            title="Ouvrir l'image en pleine taille"
            data-lightbox={true}
          >
            <ImageFromDrupal
              className={node.className}
              alt={node.alt}
              drupal_fid={node.id}
              ariaHidden={true}
            />
          </button>
        </>
      )}
      {type === "masonry" && (
        <>
          <button
            className={btnOpener}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir les images en pleine taille"
            title="Ouvrir les images en pleine taille"
            data-lightbox={true}
          >
            <div role="list">
              <Masonry
                breakpointCols={breakpointColumnsObj}
                className={node.className}
                columnClassName={node.columnClassName}
              >
                {node.images}
              </Masonry>
            </div>
          </button>
        </>
      )}

      <Lightbox
        controller={{ ref: lightboxRef }}
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        plugins={[Fullscreen, Zoom, Thumbnails]}
        thumbnails={{
          position: "bottom",
          width: 120,
          height: 80,
          border: 0,
          padding: 0,
        }}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 10,
        }}
        labels={{
          Close: "Fermer",
          "Enter Fullscreen": "Entrer en pleine écran",
          "Exit Fullscreen": "Sortir du pleine écran",
          "Zoom out": "Dézoomer",
          "Zoom in": "Zoomer",
          Previous: "Diapositive précédente",
          Next: "Diapositive suivante",
        }}
        styles={{
          button: { "background-color": themeColors },
        }}
        render={{
          buttonPrev: (props) => (
            <PrevButton
              lightboxRef={lightboxRef}
              slidesLength={slides.length}
            />
          ),
          buttonNext: (props) => (
            <NextButton
              lightboxRef={lightboxRef}
              slidesLength={slides.length}
            />
          ),
          slideFooter: ({ slide }) => <CaptionContainer slide={slide} />,
        }}
        on={{
          entered: () => {
            if (typeof document !== `undefined`) {
              document.querySelectorAll(".yarl__root img").forEach((img) => {
                img.setAttribute("aria-hidden", "true")
              })
            }
          },
        }}
      />
    </div>
  )
}

const LightBoxCarouselOpener = ({ images, className }) => {
  let themeColors =
    useBuildData().theme_settings.palette.a_color_gradients.a_dark
  themeColors =
    themeColors === "#FFFFFF"
      ? useBuildData().theme_settings.palette.c_color_gradients.c_dark
      : themeColors
  const [open, setOpen] = React.useState(false)
  const slides = []
  if (typeof document !== `undefined`) {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((element) => {
            if (
              element.nodeType === element.ELEMENT_NODE &&
              element.classList.contains("yarl__root")
            ) {
              element.setAttribute("role", "dialog")
              element.setAttribute("aria-modal", true)
              element.setAttribute("aria-label", "Visionneuse d'images")
            }
          })
        }
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
  }

  images.forEach((image) => {
    if (image.type === "fromDrupal") {
      const drupalImage = drupalImageContent(image.id)

      if (drupalImage !== null) {
        slides.push({
          src: drupalImage.node.localFile.SlugifiedPublicUrl,
          alt: image.alt,
          height: "700",
          imageFit: "contain",
        })
      }
    }
    if (image.type === "node") {
      slides.push({
        src: image.file ? image.file.SlugifiedPublicUrl : image.src,
        alt: image.alt,
        height: "700",
        imageFit: "contain",
      })
    }
  })

  const lightboxRef = useRef(null)

  return (
    <>
      <CarouselOpenerButton
        className={className}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir les images du carousel en pleine taille"
        title="Ouvrir les images du carousel en pleine taille"
      >
        Ouvrir en pleine taille
      </CarouselOpenerButton>
      <Lightbox
        controller={{ ref: lightboxRef }}
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        plugins={[Fullscreen, Zoom, Thumbnails]}
        thumbnails={{
          position: "bottom",
          width: 120,
          height: 80,
          border: 0,
          padding: 0,
        }}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 10,
        }}
        labels={{
          Close: "Fermer",
          "Enter Fullscreen": "Entrer en pleine écran",
          "Exit Fullscreen": "Sortir du pleine écran",
          "Zoom out": "Dézoomer",
          "Zoom in": "Zoomer",
          Previous: "Diapositive précédente",
          Next: "Diapositive suivante",
        }}
        styles={{
          button: { "background-color": themeColors },
        }}
        render={{
          buttonPrev: (props) => (
            <PrevButton
              lightboxRef={lightboxRef}
              slidesLength={slides.length}
            />
          ),
          buttonNext: (props) => (
            <NextButton
              lightboxRef={lightboxRef}
              slidesLength={slides.length}
            />
          ),
          slideFooter: ({ slide }) => <CaptionContainer slide={slide} />,
        }}
      />
    </>
  )
}

const PrevButton = ({ lightboxRef, slidesLength }) => {
  if (slidesLength <= 1) {
    return null
  }
  return (
    <PrevButtonStyled
      className="yarl__button yarl__navigation_prev"
      onClick={() => lightboxRef.current?.prev()}
      title="Diapositive précédente"
      aria-label="Diapositive précédente"
      type="button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        aria-hidden="true"
        focusable="false"
        className="yarl__icon"
      >
        <g fill="currentColor">
          <path d="M0 0h24v24H0z" fill="none"></path>
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
        </g>
      </svg>
    </PrevButtonStyled>
  )
}

const NextButton = ({ lightboxRef, slidesLength }) => {
  if (slidesLength <= 1) {
    return null
  }
  return (
    <NextButtonStyled
      className="yarl__button yarl__navigation_next"
      onClick={() => lightboxRef.current?.next()}
      title="Diapositive suivante"
      aria-label="Diapositive suivante"
      type="button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        aria-hidden="true"
        focusable="false"
        className="yarl__icon"
      >
        <g fill="currentColor">
          <path d="M0 0h24v24H0z" fill="none"></path>
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
        </g>
      </svg>
    </NextButtonStyled>
  )
}
const CaptionContainer = ({ slide }) => {
  return (
    <div className={captionContainer}>
      <p>{slide.alt}</p>
    </div>
  )
}

const drupalImageContent = (id) => {
  const data = useStaticQuery(
    graphql`
      query {
        images: allFileFile {
          edges {
            node {
              drupal_internal__fid
              localFile {
                extension
                SlugifiedPublicUrl
                name
              }
            }
          }
        }
      }
    `
  )
  const filteredImage = data.images.edges
    .filter(({ node }) => node.drupal_internal__fid === id)
    .shift()

  if (filteredImage && filteredImage.node.localFile) {
    return filteredImage
  }
  return null
}

LightBoxImage.propTypes = {
  node: PropTypes.shape({
    alt: PropTypes.string,
    file: PropTypes.object,
    src: PropTypes.string,
    style: PropTypes.object,
    id: PropTypes.number,
    images: PropTypes.object,
    className: PropTypes.string,
    columnClassName: PropTypes.string,
    containerStyle: PropTypes.object,
  }).isRequired,
  type: PropTypes.oneOf(["node", "fromDrupal", "carousel", "masonry"])
    .isRequired,
}

LightBoxCarouselOpener.propTypes = {
  images: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
}

PrevButton.propTypes = {
  lightboxRef: PropTypes.object.isRequired,
  slidesLength: PropTypes.number.isRequired,
}

NextButton.propTypes = {
  lightboxRef: PropTypes.object.isRequired,
  slidesLength: PropTypes.number.isRequired,
}

CaptionContainer.propTypes = {
  slide: PropTypes.object.isRequired,
}

export { LightBoxImage, LightBoxCarouselOpener }
